/**
 * AdminExportPack.jsx — Page admin pour exporter les packs d'intégration
 * vers un site partenaire (codevoiture.fr).
 *
 * Route : /admin/annuaire/export (admin only)
 *
 * Récupère EXPORT_SECRET via l'endpoint /api/admin/export-pack/credentials
 * (admin JWT required) puis génère un message prêt-à-envoyer avec les
 * commandes curl incluant le secret.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Copy, CheckCircle2, Shield, Download, Loader2, RefreshCw, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';

export default function AdminExportPack() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showSecret, setShowSecret] = useState(false);
  const [copied, setCopied] = useState(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL || ''}/api/admin/export-pack/credentials`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!res.ok) {
        const txt = await res.text();
        throw new Error(`HTTP ${res.status} · ${txt.slice(0, 200)}`);
      }
      const json = await res.json();
      setData(json);
    } catch (e) {
      setError(e.message || 'Erreur inconnue');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const copyText = (text, label) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(label);
      toast.success(`${label} copié dans le presse-papiers`);
      setTimeout(() => setCopied(null), 2500);
    }).catch(() => toast.error('Impossible de copier'));
  };

  const maskedSecret = data?.secret
    ? data.secret.slice(0, 4) + '•'.repeat(Math.max(0, data.secret.length - 8)) + data.secret.slice(-4)
    : '';

  const fullMessage = data ? `📦 INTÉGRATION 2 PACKS depuis code2roues.fr

Bonjour,

Voici les 2 packs d'intégration prêts à télécharger (secret partagé
inclus — ne pas diffuser publiquement).

═══════════════════════════════════════════════════════════════════════
📚 PACK LIVRET AM TOPOLINO (voiturette · 68 pages PDF + flipbook)
═══════════════════════════════════════════════════════════════════════

cd /tmp && \\
${data.curl_examples.livret_am} && \\
mkdir -p /tmp/am-pack && \\
unzip -o am-pack.zip -d /tmp/am-pack && \\
cat /tmp/am-pack/README.md

═══════════════════════════════════════════════════════════════════════
🗺️ PACK ANNUAIRE (recherche · carte Leaflet · admin · claim)
═══════════════════════════════════════════════════════════════════════

cd /tmp && \\
${data.curl_examples.annuaire} && \\
mkdir -p /tmp/annuaire-pack && \\
unzip -o annuaire-pack.zip -d /tmp/annuaire-pack && \\
cat /tmp/annuaire-pack/README.md

═══════════════════════════════════════════════════════════════════════

Toute la doc technique (adaptation MOTO→VOITURE pour l'annuaire, routes
à ajouter, dépendances pip/yarn, commandes de peuplement data.gouv.fr)
est dans les README.md des 2 ZIP.

Note sécurité : ce secret est confidentiel. Si vous suspectez une
fuite, demandez-moi de le régénérer.

Merci !` : '';

  return (
    <div className="space-y-5 max-w-5xl" data-testid="admin-export-pack-page">
      <div className="flex items-center gap-3">
        <Link to="/admin/annuaire" className="text-muted-foreground hover:text-foreground inline-flex items-center gap-1.5 text-sm" data-testid="btn-back-admin-annuaire">
          <ArrowLeft className="w-4 h-4" /> Annuaire admin
        </Link>
      </div>

      <div>
        <div className="inline-flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest text-amber-500 mb-1">
          <Shield className="w-3.5 h-3.5" /> Admin only · Secret partagé
        </div>
        <h1 className="text-2xl sm:text-3xl font-black tracking-tight">Exporter vers un site partenaire</h1>
        <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
          Génère un message prêt-à-envoyer contenant les commandes <code className="text-[11px] bg-muted px-1 rounded">curl</code> + le secret <code className="text-[11px] bg-muted px-1 rounded">EXPORT_SECRET</code> pour que le site frère puisse télécharger les packs d&apos;intégration (annuaire + livret AM).
        </p>
      </div>

      {loading && (
        <div className="rounded-xl border bg-card p-6 flex items-center gap-3 text-sm text-muted-foreground">
          <Loader2 className="w-4 h-4 animate-spin" /> Récupération du secret...
        </div>
      )}

      {error && (
        <div className="rounded-xl border border-destructive/40 bg-destructive/5 p-6 text-sm" data-testid="export-pack-error">
          <div className="font-black text-destructive mb-1">Erreur</div>
          <div className="text-destructive/90 mb-3">{error}</div>
          <Button variant="outline" size="sm" onClick={load}><RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Réessayer</Button>
        </div>
      )}

      {data && (
        <>
          {/* Secret affiché */}
          <section className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-5" data-testid="export-pack-secret-block">
            <div className="flex items-start justify-between gap-3 mb-3">
              <div>
                <div className="text-[10px] font-black uppercase tracking-widest text-amber-500 mb-1">⚠ Confidentiel</div>
                <h2 className="text-lg font-black">EXPORT_SECRET</h2>
                <p className="text-[12px] text-muted-foreground mt-0.5">
                  Pour renouveler : modifier <code className="text-[11px] bg-muted px-1 rounded">EXPORT_SECRET</code> dans <code className="text-[11px] bg-muted px-1 rounded">backend/.env</code> puis redémarrer le backend.
                </p>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setShowSecret(s => !s)} data-testid="btn-toggle-secret">
                {showSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </Button>
            </div>
            <div className="flex items-center gap-2">
              <code className="flex-1 px-3 py-2 rounded-md bg-black/80 text-amber-300 text-[12px] font-mono break-all" data-testid="export-pack-secret-value">
                {showSecret ? data.secret : maskedSecret}
              </code>
              <Button size="sm" variant="outline" onClick={() => copyText(data.secret, 'Secret')} data-testid="btn-copy-secret">
                {copied === 'Secret' ? <CheckCircle2 className="w-4 h-4 text-emerald-500" /> : <Copy className="w-4 h-4" />}
              </Button>
            </div>
          </section>

          {/* Commandes curl */}
          <section className="rounded-xl border bg-card p-5" data-testid="export-pack-curl-block">
            <h2 className="text-lg font-black mb-3">Commandes curl prêtes à l&apos;emploi</h2>

            <div className="space-y-4">
              <div>
                <div className="text-[11px] font-black uppercase tracking-widest text-muted-foreground mb-1.5">📚 Livret AM Topolino</div>
                <div className="flex items-start gap-2">
                  <code className="flex-1 px-3 py-2.5 rounded-md bg-black/90 text-emerald-300 text-[11px] font-mono whitespace-pre-wrap break-all" data-testid="curl-livret-am">
                    {data.curl_examples.livret_am}
                  </code>
                  <Button size="sm" variant="outline" onClick={() => copyText(data.curl_examples.livret_am, 'Curl Livret AM')} data-testid="btn-copy-curl-livret">
                    {copied === 'Curl Livret AM' ? <CheckCircle2 className="w-4 h-4 text-emerald-500" /> : <Copy className="w-4 h-4" />}
                  </Button>
                </div>
              </div>

              <div>
                <div className="text-[11px] font-black uppercase tracking-widest text-muted-foreground mb-1.5">🗺️ Annuaire</div>
                <div className="flex items-start gap-2">
                  <code className="flex-1 px-3 py-2.5 rounded-md bg-black/90 text-emerald-300 text-[11px] font-mono whitespace-pre-wrap break-all" data-testid="curl-annuaire">
                    {data.curl_examples.annuaire}
                  </code>
                  <Button size="sm" variant="outline" onClick={() => copyText(data.curl_examples.annuaire, 'Curl Annuaire')} data-testid="btn-copy-curl-annuaire">
                    {copied === 'Curl Annuaire' ? <CheckCircle2 className="w-4 h-4 text-emerald-500" /> : <Copy className="w-4 h-4" />}
                  </Button>
                </div>
              </div>
            </div>

            <div className="mt-4 pt-4 border-t text-[11px] text-muted-foreground">
              Base URL : <code className="bg-muted px-1 rounded">{data.base_url}</code>
            </div>
          </section>

          {/* Message complet */}
          <section className="rounded-xl border bg-card p-5" data-testid="export-pack-message-block">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-lg font-black">Message prêt à envoyer</h2>
              <Button size="sm" onClick={() => copyText(fullMessage, 'Message complet')} data-testid="btn-copy-full-message">
                {copied === 'Message complet' ? <><CheckCircle2 className="w-4 h-4 mr-1.5 text-emerald-500" /> Copié !</> : <><Copy className="w-4 h-4 mr-1.5" /> Copier le message complet</>}
              </Button>
            </div>
            <p className="text-[12px] text-muted-foreground mb-3">
              Collez ce message dans le chat de l&apos;agent qui gère le site partenaire (codevoiture.fr). Il contient tout : les 2 commandes curl avec secret inclus, les étapes d&apos;extraction et un rappel vers le README.
            </p>
            <pre className="px-3 py-3 rounded-md bg-black/90 text-neutral-200 text-[10.5px] font-mono whitespace-pre-wrap max-h-[400px] overflow-auto" data-testid="export-pack-full-message">
              {fullMessage}
            </pre>
          </section>

          {/* Lien direct de téléchargement (admin) */}
          <section className="rounded-xl border bg-card p-5" data-testid="export-pack-direct-download">
            <h2 className="text-lg font-black mb-2">Téléchargement direct (admin)</h2>
            <p className="text-[12px] text-muted-foreground mb-3">
              Pour télécharger vous-même les ZIP depuis ce navigateur (le secret est ajouté en query param — ne diffusez pas ces URLs) :
            </p>
            <div className="flex flex-wrap gap-2">
              <Button asChild variant="outline" size="sm">
                <a href={`${process.env.REACT_APP_BACKEND_URL || ''}/api/livrets/am-8h-2026/export-pack?secret=${encodeURIComponent(data.secret)}`} target="_blank" rel="noopener noreferrer" data-testid="btn-download-livret-am-pack-admin">
                  <Download className="w-3.5 h-3.5 mr-1.5" /> Livret AM (ZIP)
                </a>
              </Button>
              <Button asChild variant="outline" size="sm">
                <a href={`${process.env.REACT_APP_BACKEND_URL || ''}/api/annuaire/export-pack?secret=${encodeURIComponent(data.secret)}`} target="_blank" rel="noopener noreferrer" data-testid="btn-download-annuaire-pack-admin">
                  <Download className="w-3.5 h-3.5 mr-1.5" /> Annuaire (ZIP)
                </a>
              </Button>
            </div>
          </section>
        </>
      )}
    </div>
  );
}
